"use client";

import React, { useState } from "react";
import Image from "next/image";
import {
  Box,
  Typography,
  Dialog
} from "@mui/material";
import { AssetFields, FooterFields } from "@/types/contentful/footer";
import { Company } from "@/types/contentful/company";
import useScrollLock from "@/hooks/scroll-lock/use-scroll-lock";
import {ColletteButton} from "@collette-travel/collette-common-controls";
import RequestACallbackSection from "@/components/forms/request-a-callback/request-a-callback-section";
import TalkToAnExpertSection from "@/components/forms/talk-to-an-expert/talk-to-an-expert-section";
import {useTranslations} from "next-intl";

interface CustomerServiceClientProps {
  footerData: FooterFields[];
  companyData: Company[];
}


const CustomerService: React.FC<CustomerServiceClientProps> = ({ footerData, companyData }) => {
  const t = useTranslations("Footer");

  const [open, setOpen] = useState(false);
  const [formType, setFormType] = useState("RAC");

  useScrollLock(open);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = (type: "RAC" | "TTAE") => {
    setFormType(type);
    setOpen(true);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        flexDirection: { xs: "column", md: "row" }
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          position: "relative",
          width: "100%",
          height: "auto",
          order: { xs: 2, md: 1 }
        }}
      >
        <Image
          src={`https:${
            (footerData[0]?.customerService?.fields?.image?.fields as unknown as AssetFields)?.file
              ?.url
              ? `${
                  (footerData[0].customerService.fields.image.fields as unknown as AssetFields).file
                    .url
                }`
              : "/images/friends-mt-fuji.jpg"
          }`}
          alt="Friends at Mt. Fuji"
          width={1200}
          height={500}
          priority
          style={{
            maxWidth: "100%",
            height: "auto"
          }}
        />
      </Box>

      <Box
        sx={{
          backgroundColor: { xs: "background.brand", md: "background.default" },
          flex: 1,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 5,
          order: { xs: 1, md: 2 }
        }}
      >
        <Typography variant="h2" fontSize="36px" sx={{ color: { xs: "var(--theme-palette-common-white)", md: "var(--theme-palette-text-primary)" } }}>
          {footerData[0]?.customerService?.fields?.title as unknown as string}
        </Typography>
        <Typography
          className="disable-tel-link"
          variant="h3"
          fontSize="24px"
          sx={{
            color: { xs: "var(--theme-palette-common-white)", md: "var(--theme-palette-text-primary)" },
            padding: "10px 0px 20px 0px"
          }}
          data-testid="call-us-number-footer"
        >
          {t.rich("Call us toll free at", {
            phone: companyData[0]?.phoneNumber,
            // eslint-disable-next-line react/no-unstable-nested-components
            span: (chunks) => (
              <span style={{
                fontWeight: "bold",
                color: "inherit",
                textDecoration: "none"
              }}>
                {chunks}
              </span>
            )})
          }
        </Typography>

        <Box sx={{ marginBottom: 2 }}>
          <ColletteButton
            variant="outlined"
            size="large"
            onClick={() => handleOpen("RAC")}
            testId="request-call-back-button"
            label={footerData[0]?.customerService?.fields?.racButtonText as unknown as string}
            sx={{
              backgroundColor: "background.default"
            }}
          />
        </Box>

        <Box sx={{ marginBottom: 2 }}>
          <ColletteButton
            variant="outlined"
            size="large"
            onClick={() => handleOpen("TTAE")}
            testId="talk-to-expert-button"
            label={footerData[0]?.customerService?.fields?.ttaeButtonText as unknown as string}
            sx={{
              backgroundColor: "background.default"
            }}
          />
        </Box>
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth aria-modal="true">
        {formType === "RAC" && <RequestACallbackSection handleClose={handleClose} />}
        {formType === "TTAE" && <TalkToAnExpertSection handleClose={handleClose} />}
      </Dialog>
    </Box>
  );
};

export default CustomerService;
